/**
 * @file `buildProfilePatch` — derive the minimal operator profile-edit patch
 *       from the drawer's form draft (#1639).
 * @module @labcraft/admin/ui/learners/profile-patch
 *
 * Pure (no JSX) so the unit tests can import it — the drawer's `.tsx` can't
 * load under Node's type-stripping. Encodes the load-bearing edit rules: only
 * changed, non-empty fields become a patch (an empty patch means nothing to
 * save), and `email` is never in the draft, so it can never be written — the
 * read-only-email invariant holds by construction.
 */

import type {
  Learner,
  UpdateLearnerProfileRequest,
} from '../../shared/routes.ts'

/**
 * The drawer's editable profile fields as free-text form state. `email` is
 * deliberately absent — it is read-only.
 *
 * @property displayName - The display-name input value.
 * @property timezone - The timezone input value.
 * @property avatarUrl - The avatar-URL input value.
 */
export type ProfileDraft = {
  displayName: string
  timezone: string
  avatarUrl: string
}

/**
 * Build the minimal profile-edit patch: include a field only when its
 * trimmed draft value is non-empty AND differs from the learner's current
 * value. An unchanged or blanked field is omitted (the PATCH leaves absent
 * fields untouched — there is no "clear a field" affordance). `email` is not
 * a `ProfileDraft` field, so it is structurally impossible to include.
 *
 * @param learner - The current learner row (the baseline to diff against).
 * @param draft - The drawer's form state.
 * @returns The partial edit; `{}` when nothing changed.
 */
export function buildProfilePatch(
  learner: Learner,
  draft: ProfileDraft,
): UpdateLearnerProfileRequest {
  const patch: UpdateLearnerProfileRequest = {}

  const displayName = draft.displayName.trim()
  if (displayName !== '' && displayName !== learner.displayName) {
    patch.displayName = displayName
  }

  const timezone = draft.timezone.trim()
  if (timezone !== '' && timezone !== (learner.timezone ?? '')) {
    patch.timezone = timezone
  }

  const avatarUrl = draft.avatarUrl.trim()
  if (avatarUrl !== '' && avatarUrl !== (learner.avatarUrl ?? '')) {
    patch.avatarUrl = avatarUrl
  }

  return patch
}
